import { env } from 'features/env';
import { getAccessTokenWithFallback } from 'features/communication-bridge/tokenStore';

const SERVICE_URLS_BY_SERVICE = {
	cde: env.REACT_APP_API_URL_DATAENTRY,
	projects: env.REACT_APP_API_URL_PROJECT,
	stats: env.REACT_APP_API_URL_STATS,
	user: env.REACT_APP_API_URL_USER,
	userAccount: env.REACT_APP_API_URL_USER_ACCOUNT
};

type Service = keyof typeof SERVICE_URLS_BY_SERVICE;

type ResponseStatusFields = {
	statusCode?: string | number;
	httpStatusCode?: number;
	ledidiStatusCode?: string | number;
};

const parseStatusCode = (json: ResponseStatusFields): number | undefined => {
	if (json?.statusCode !== undefined) {
		const parsed = Number(json.statusCode);
		return Number.isNaN(parsed) ? undefined : parsed;
	}

	if (json?.httpStatusCode !== undefined) {
		return json.httpStatusCode;
	}

	if (json?.ledidiStatusCode !== undefined) {
		const parsed = Number(json.ledidiStatusCode);
		return Number.isNaN(parsed) ? undefined : parsed;
	}

	return undefined;
};

export class LedidiRequestError extends Error {
	errors: {
		code: string;
	}[];
	service: string;
	method: string;
	traceId?: string;

	constructor({
		service,
		method,
		errors,
		traceId
	}: {
		service: string;
		method: string;
		errors: { code: string }[];
		traceId?: string;
	}) {
		super(`LedidiRequestError: ${service} ${method}`);
		this.errors = errors;
		this.service = service;
		this.method = method;
		this.traceId = traceId;
	}
}

export const makeRequest = async <T>({
	data,
	method,
	service,
	validateCredentials = true
}: {
	data?: any;
	method: string;
	service: Service;
	validateCredentials?: boolean;
}): Promise<T> => {
	const accessTokenStr = await getAccessTokenWithFallback();
	if (validateCredentials && !accessTokenStr) {
		throw new LedidiRequestError({
			service,
			method,
			errors: [{ code: 'error.httpError.401' }]
		});
	}

	const result = await fetch(SERVICE_URLS_BY_SERVICE[service], {
		method: 'POST',
		body: JSON.stringify({
			method,
			accessTokenStr,
			...data
		}),
		headers: {
			'Content-Type': 'application/json'
		}
	});

	if (!result.ok) {
		throw new LedidiRequestError({
			service,
			method,
			errors: [{ code: `error.httpError.${result.status}` }]
		});
	}

	const json: T & {
		statusCode?: string | number;
		httpStatusCode?: number;
		ledidiStatusCode?: string;
		message?: string;
		errors?: {
			code: string;
		}[];
		traceId?: string;
	} = await result.json();

	// Status priority for the backend response:
	// 1. statusCode if exists
	// 2. httpStatusCode if exists
	// 3. ledidiStatusCode if exists
	// 4. undefined otherwise.
	const statusCode = parseStatusCode(json);

	// Check if there are errors in the response body
	if (json.errors && json.errors.length > 0) {
		throw new LedidiRequestError({
			service,
			method,
			errors: json.errors,
			traceId: json.traceId
		});
	}

	// Check if the status code indicates an error
	if (statusCode !== undefined && (Number(statusCode) < 200 || Number(statusCode) >= 300)) {
		// If the error code is not already in the errors array, add it so we can handle errors in a consistent way later
		if (!json.errors?.find(e => e.code === json.ledidiStatusCode)) {
			json.errors = json.errors ?? [];
			json.errors.push({
				code: json.ledidiStatusCode ?? String(json.statusCode)
			});
		}

		if (json.errors) {
			throw new LedidiRequestError({
				service,
				method,
				errors: json.errors,
				traceId: json.traceId
			});
		}
	}

	return json;
};
